import type { FileImportRow, FileImportRowOutcome, ImportDeductionsResponse, ImportSkipReason } from "@/api/file_import"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  ImportRowDetailGrid,
  import_row_display_fields,
} from "@/file_imports/import_row_outcome_display"
import type { JSX } from "preact"
import { useMemo } from "preact/hooks"

const SKIP_REASON_ORDER: ImportSkipReason[] = [
  "already_linked",
  "missing_invoice_number",
  "missing_kind",
  "missing_source",
  "bc_group_missing_source",
  "bc_group_missing_kind",
]

export function import_skip_reason_title(reason: ImportSkipReason): string {
  switch (reason) {
    case "already_linked":
      return "Already linked"
    case "missing_invoice_number":
      return "Missing invoice number"
    case "missing_kind":
      return "Missing kind"
    case "missing_source":
      return "Missing source"
    case "bc_group_missing_source":
      return "BC SoT group missing source"
    case "bc_group_missing_kind":
      return "BC SoT group missing kind"
    default: {
      const _exhaustive: never = reason
      return _exhaustive
    }
  }
}

export function import_skip_reason_label(reason: ImportSkipReason): string {
  switch (reason) {
    case "already_linked":
      return "Already linked to a deduction (nothing to import for this row)."
    case "missing_invoice_number":
      return "Missing invoice number — edit the row or fix the file so an invoice can be parsed."
    case "missing_kind":
      return "Missing transaction kind (e.g. deduction vs repayment) for this row."
    case "missing_source":
      return "Missing distributor / source — map the customer or fix the parsed source."
    case "bc_group_missing_source":
      return "BC SoT group could not be imported: first line in the group has no source."
    case "bc_group_missing_kind":
      return "BC SoT group could not be imported: first line in the group has no kind."
    default: {
      const _exhaustive: never = reason
      return _exhaustive
    }
  }
}

function group_skipped_by_reason(rows: FileImportRowOutcome[]): Map<ImportSkipReason, FileImportRowOutcome[]> {
  const grouped = new Map<ImportSkipReason, FileImportRowOutcome[]>()
  for (const o of rows) {
    if (o.outcome.kind !== "skipped") {
      continue
    }
    const reason = o.outcome.reason
    const list = grouped.get(reason) ?? []
    list.push(o)
    grouped.set(reason, list)
  }
  return grouped
}

interface OutcomeRowCardProps {
  outcome: FileImportRowOutcome
  row: FileImportRow | undefined
  headers: string[]
  renderTrailing?(o: FileImportRowOutcome): JSX.Element
}

function OutcomeRowCard(props: OutcomeRowCardProps) {
  const { outcome, row, headers, renderTrailing } = props
  const fields = import_row_display_fields(row, headers)
  const title = fields.invoice ?? outcome.invoice_hint?.trim() ?? "—"

  return (
    <li class="px-3 py-2.5 border-b border-gray-100 last:border-b-0">
      <div class="flex flex-wrap items-start gap-x-3 gap-y-1">
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span class="text-sm font-medium text-gray-900">{title}</span>
            {fields.linked_deduction_id ? (
              <a
                href={`/deduction/${fields.linked_deduction_id}`}
                class="text-xs text-blue-600 hover:underline shrink-0">
                Linked deduction
              </a>
            ) : null}
          </div>
          <ImportRowDetailGrid fields={fields} />
        </div>
        {renderTrailing ? <span class="shrink-0 pt-0.5">{renderTrailing(outcome)}</span> : null}
      </div>
    </li>
  )
}

interface OutcomeRowListProps {
  outcomes: FileImportRowOutcome[]
  rows_by_id: Map<string, FileImportRow>
  headers: string[]
  renderTrailing?(o: FileImportRowOutcome): JSX.Element
}

function OutcomeRowList(props: OutcomeRowListProps) {
  const { outcomes, rows_by_id, headers, renderTrailing } = props
  return (
    <ul class="border border-gray-200 rounded-md divide-y divide-gray-100 text-sm bg-white">
      {outcomes.map((o: FileImportRowOutcome) => (
        <OutcomeRowCard
          key={o.row_id}
          outcome={o}
          row={rows_by_id.get(o.row_id)}
          headers={headers}
          renderTrailing={renderTrailing}
        />
      ))}
    </ul>
  )
}

interface OutcomeSectionProps {
  title: string
  emptyMessage: string
  outcomes: FileImportRowOutcome[]
  rows_by_id: Map<string, FileImportRow>
  headers: string[]
  row_ids: string[]
  onViewInTable(row_ids: string[]): void
  renderTrailing?(o: FileImportRowOutcome): JSX.Element
}

function OutcomeSection(props: OutcomeSectionProps) {
  const { title, emptyMessage, outcomes, rows_by_id, headers, row_ids, onViewInTable, renderTrailing } = props
  return (
    <section class="mb-6 last:mb-0">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 class="text-sm font-semibold text-gray-900">{title}</h3>
        {outcomes.length > 0 ? (
          <Button type="button" variant="outline" size="sm" class="h-7 text-xs" onClick={() => onViewInTable(row_ids)}>
            View in table ({outcomes.length})
          </Button>
        ) : null}
      </div>
      {outcomes.length === 0 ? (
        <p class="text-sm text-gray-500">{emptyMessage}</p>
      ) : (
        <OutcomeRowList outcomes={outcomes} rows_by_id={rows_by_id} headers={headers} renderTrailing={renderTrailing} />
      )}
    </section>
  )
}

interface SkippedByReasonSectionProps {
  skipped: FileImportRowOutcome[]
  rows_by_id: Map<string, FileImportRow>
  headers: string[]
  onViewInTable(row_ids: string[]): void
}

function SkippedByReasonSection(props: SkippedByReasonSectionProps) {
  const { skipped, rows_by_id, headers, onViewInTable } = props
  if (skipped.length === 0) {
    return (
      <section class="mb-0">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">Skipped rows</h3>
        <p class="text-sm text-gray-500">No rows were skipped.</p>
      </section>
    )
  }

  const grouped = group_skipped_by_reason(skipped)

  return (
    <section class="mb-0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Skipped rows</h3>
      <div class="space-y-5">
        {SKIP_REASON_ORDER.map((reason: ImportSkipReason) => {
          const group_outcomes = grouped.get(reason)
          if (!group_outcomes || group_outcomes.length === 0) {
            return null
          }
          const group_row_ids = group_outcomes.map((o: FileImportRowOutcome) => o.row_id)
          return (
            <div key={reason} class="border border-amber-200/80 rounded-lg bg-amber-50/40 overflow-hidden">
              <div class="px-3 py-2 border-b border-amber-200/60 bg-amber-50/80">
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="min-w-0">
                    <h4 class="text-sm font-medium text-gray-900">
                      {import_skip_reason_title(reason)}{" "}
                      <span class="text-gray-600 font-normal">
                        ({group_outcomes.length} row{group_outcomes.length === 1 ? "" : "s"})
                      </span>
                    </h4>
                    <p class="text-xs text-gray-600 mt-0.5 leading-snug">{import_skip_reason_label(reason)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    class="h-7 text-xs shrink-0 bg-white"
                    onClick={() => onViewInTable(group_row_ids)}>
                    View in table
                  </Button>
                </div>
              </div>
              <div class="max-h-64 overflow-y-auto">
                <OutcomeRowList
                  outcomes={group_outcomes}
                  rows_by_id={rows_by_id}
                  headers={headers}
                />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export interface ImportDeductionResultsDialogProps {
  result: ImportDeductionsResponse | null
  rows: FileImportRow[]
  headers: string[]
  onOpenChange(open: boolean): void
  onViewRowsInTable(row_ids: string[]): void
}

export function ImportDeductionResultsDialog(props: ImportDeductionResultsDialogProps) {
  const { result, rows, headers, onOpenChange, onViewRowsInTable } = props
  const open = result !== null
  const rowOutcomes = result?.row_outcomes ?? []

  const rows_by_id = useMemo(() => {
    const map = new Map<string, FileImportRow>()
    for (const row of rows) {
      map.set(row.id, row)
    }
    return map
  }, [rows])

  const created = rowOutcomes.filter((o: FileImportRowOutcome) => o.outcome.kind === "created_deduction")
  const linked = rowOutcomes.filter((o: FileImportRowOutcome) => o.outcome.kind === "linked_existing_deduction")
  const skipped = rowOutcomes.filter((o: FileImportRowOutcome) => o.outcome.kind === "skipped")

  const created_ids = created.map((o: FileImportRowOutcome) => o.row_id)
  const linked_ids = linked.map((o: FileImportRowOutcome) => o.row_id)
  function handleViewInTable(row_ids: string[]) {
    onViewRowsInTable(row_ids)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {result ? (
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw*2/3)] max-w-[calc(100vw*2/3)] flex-col gap-0 overflow-hidden p-6">
          <DialogHeader className="shrink-0">
            <DialogTitle>Import results</DialogTitle>
          </DialogHeader>
          <p class="text-sm text-gray-600 mb-4 shrink-0">
            {result.imported_count} new deduction{result.imported_count === 1 ? "" : "s"} · {result.rows_linked_count}{" "}
            row{result.rows_linked_count === 1 ? "" : "s"} linked · {result.skipped_count} skipped
          </p>
          <div class="flex-1 min-h-0 overflow-y-auto pr-1">
            <OutcomeSection
              title="Created deductions"
              emptyMessage="No new deductions were created."
              outcomes={created}
              rows_by_id={rows_by_id}
              headers={headers}
              row_ids={created_ids}
              onViewInTable={handleViewInTable}
              renderTrailing={(o: FileImportRowOutcome) =>
                o.outcome.kind === "created_deduction" ? (
                  <a href={`/deduction/${o.outcome.deduction_id}`} class="text-blue-600 hover:underline text-sm">
                    Open deduction
                  </a>
                ) : (
                  <span />
                )
              }
            />
            <OutcomeSection
              title="Linked to existing deductions"
              emptyMessage="No rows were attached to an existing deduction."
              outcomes={linked}
              rows_by_id={rows_by_id}
              headers={headers}
              row_ids={linked_ids}
              onViewInTable={handleViewInTable}
              renderTrailing={(o: FileImportRowOutcome) =>
                o.outcome.kind === "linked_existing_deduction" ? (
                  <a href={`/deduction/${o.outcome.deduction_id}`} class="text-blue-600 hover:underline text-sm">
                    Open deduction
                  </a>
                ) : (
                  <span />
                )
              }
            />
            <SkippedByReasonSection
              skipped={skipped}
              rows_by_id={rows_by_id}
              headers={headers}
              onViewInTable={handleViewInTable}
            />
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}
